import React, { useEffect, useRef, useState } from "react";
import Select, { type Theme } from "react-select";
import { useNavigate, useOutletContext } from "react-router";
import {
  ICmsQuestionType,
  ICmsSubject,
  ITeacherQuestion,
  ITeacherQuestionSearchRequest,
  getCmsQuestionTypes,
  getCmsTeacherSubjects,
  getTeacherQuestions,
} from "../../../../api/crmContent.service";
import { getAllDomainsBySubject } from "../../../../api/domain.service";
import { getStandardOptionsByDomain } from "../../../../api/standard.service";
import {
  EditIcon,
  FilterIcon,
  PreviewIcon,
  ResetIcon,
} from "../../../../assets/icons/index";
import { ISelectElement } from "../../../../model/interface/keyValuePair";

import Loader from "../../../shared/loader/loader";
import Pagination from "../../../shared/common/pagination";
import SelectPageSize from "../../../shared/common/pageSizeDropdown";
import { SortIcon } from "../../../shared/common/sortIcon";
import { EmptyResults } from "../../../shared/common/emptyResults";
import { InitialsBadge } from "../../../shared/common/initialsBadge";
import Constant from "../../../../utils/constant";
import { dokOptions, IDokOption } from "../../../../utils/dokOptions";
import { IUserContentOutletContext } from "./UserContentLayout";
import { toast } from "react-toastify";
import PreviewModal from "../../../screen/questions/previewModal";
import {
  buildQuestionSearchRequest,
  IQuestionSearchCriteria,
  IQuestionSearchInput,
  QuestionSearchMode,
  sanitizeQuestionIdInput,
} from "./questionHelpers";

const defaultPageSize = 25;
const defaultPageNumber = 1;
const defaultOrderByColumn = "CreatedDate";
const defaultSortByDesc = true;

const publishedNextState = (
  current: boolean | undefined
): boolean | undefined => {
  if (current === undefined) return true;
  if (current === true) return false;
  return undefined;
};

const publishedLabel = (current: boolean | undefined): string => {
  if (current === undefined) return "Any";
  return current ? "Yes" : "No";
};

const reactSelectTheme = (theme: Theme): Theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: "#B3B3B3",
    primary: "#041E42",
  },
});

export const Questions: React.FC = () => {
  const { userId, userInfo } = useOutletContext<IUserContentOutletContext>();
  const districtId = userInfo?.districtId;
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [teacherQuestions, setTeacherQuestions] = useState<
    Array<ITeacherQuestion>
  >([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [subjects, setSubjects] = useState<Array<ISelectElement>>([]);
  const [domains, setDomains] = useState<Array<ISelectElement>>([]);
  const [standards, setStandards] = useState<Array<ISelectElement>>([]);
  const [questionTypes, setQuestionTypes] = useState<Array<ISelectElement>>([]);

  const [searchMode, setSearchMode] = useState<QuestionSearchMode>("criteria");
  const [questionIdText, setQuestionIdText] = useState<string>("");

  const [selectedSubject, setSelectedSubject] = useState<ISelectElement | null>(
    null
  );
  const [selectedDomain, setSelectedDomain] = useState<ISelectElement | null>(
    null
  );
  const [selectedStandard, setSelectedStandard] =
    useState<ISelectElement | null>(null);
  const [selectedDok, setSelectedDok] = useState<IDokOption | null>(null);
  const [selectedQuestionType, setSelectedQuestionType] =
    useState<ISelectElement | null>(null);
  const [isPublished, setIsPublished] = useState<boolean | undefined>(
    undefined
  );

  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [pageNumber, setPageNumber] = useState<number>(defaultPageNumber);
  const [orderByColumn, setOrderByColumn] =
    useState<string>(defaultOrderByColumn);
  const [sortByDesc, setSortByDesc] = useState<boolean>(defaultSortByDesc);

  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewItemRef, setPreviewItemRef] = useState<string>("");
  const [questionIdPreview, setQuestionIdPreview] = useState<number>();
  const [questionIndexPreview, setQuestionIndexPreview] = useState<number>();

  const paginationRef = useRef<{ onReset: () => void }>(null);
  const pageSizeRef = useRef<{ onReset: () => void }>(null);
  const pageNumberInitial = useRef<boolean>(true);
  const pageSizeInitial = useRef<boolean>(true);

  const currentCriteria = (): IQuestionSearchCriteria => ({
    subjectId: selectedSubject ? Number(selectedSubject.value) : undefined,
    domainId: selectedDomain ? Number(selectedDomain.value) : undefined,
    standardId: selectedStandard ? Number(selectedStandard.value) : undefined,
    dok: selectedDok ? selectedDok.value : undefined,
    isPublished,
    questionTypeId: selectedQuestionType
      ? Number(selectedQuestionType.value)
      : undefined,
  });

  const buildRequest = (
    overrides?: Partial<IQuestionSearchInput>
  ): ITeacherQuestionSearchRequest | null =>
    buildQuestionSearchRequest({
      mode: searchMode,
      questionIdText,
      criteria: currentCriteria(),
      pageSize,
      pageNumber,
      orderByColumn,
      sortByDesc,
      districtId,
      ...overrides,
    });

  // The unfiltered first page, used on load and on Reset.
  const buildDefaultRequest = (): ITeacherQuestionSearchRequest | null =>
    buildQuestionSearchRequest({
      mode: "criteria",
      questionIdText: "",
      criteria: {},
      pageSize: defaultPageSize,
      pageNumber: defaultPageNumber,
      orderByColumn: defaultOrderByColumn,
      sortByDesc: defaultSortByDesc,
      districtId,
    });

  const search = (request: ITeacherQuestionSearchRequest | null) => {
    // null means "send nothing": the district has not loaded yet, or Question
    // ID mode has no ID entered.
    if (!request) return;
    setLoading(true);
    getTeacherQuestions(request)
      .then((response) => {
        setTeacherQuestions(response.data.data);
        setTotalRecords(response.data.totalRecords);
        setTotalPages(Math.ceil(response.data.totalRecords / request.pageSize));
      })
      .catch(() => toast.error("Failed to load questions."))
      .finally(() => setLoading(false));
  };

  const bindSubjects = () => {
    if (!userId) return;
    getCmsTeacherSubjects(userId).then((response) => {
      const options: Array<ISelectElement> = (response.data ?? []).map(
        (subject: ICmsSubject) => ({
          value: String(subject.subjectId),
          label: subject.name,
        })
      );
      setSubjects(options);
    });
  };

  const bindQuestionTypes = () => {
    getCmsQuestionTypes().then((response) => {
      const options: Array<ISelectElement> = (response.data ?? []).map(
        (qt: ICmsQuestionType) => ({
          value: String(qt.questionTypeId),
          label: qt.displayName ?? qt.name,
        })
      );
      setQuestionTypes(options);
    });
  };

  useEffect(() => {
    bindSubjects();
    bindQuestionTypes();
  }, [userId]);

  useEffect(() => {
    search(buildDefaultRequest());
  }, [districtId]);

  useEffect(() => {
    if (pageNumberInitial.current) {
      pageNumberInitial.current = false;
      return;
    }
    search(buildRequest({ pageNumber }));
  }, [pageNumber]);

  useEffect(() => {
    if (pageSizeInitial.current) {
      pageSizeInitial.current = false;
      return;
    }
    paginationRef.current?.onReset();
    search(buildRequest({ pageNumber: defaultPageNumber, pageSize }));
  }, [pageSize]);

  const handleSubjectChange = (option: ISelectElement | null) => {
    setSelectedSubject(option);
    setSelectedDomain(null);
    setSelectedStandard(null);
    setDomains([]);
    setStandards([]);
    if (option) {
      getAllDomainsBySubject(Number(option.value)).then((response) => {
        const opts: Array<ISelectElement> = (response.data?.data ?? []).map(
          (d: { domainId: number; name: string }) => ({
            value: String(d.domainId),
            label: d.name,
          })
        );
        setDomains(opts);
      });
    }
  };

  const handleDomainChange = (option: ISelectElement | null) => {
    setSelectedDomain(option);
    setSelectedStandard(null);
    setStandards([]);
    if (option) {
      getStandardOptionsByDomain(Number(option.value)).then(setStandards);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    paginationRef.current?.onReset();
    pageSizeRef.current?.onReset();
    search(
      buildRequest({
        pageNumber: defaultPageNumber,
        pageSize: defaultPageSize,
      })
    );
  };

  const clearCriteria = () => {
    setSelectedSubject(null);
    setSelectedDomain(null);
    setSelectedStandard(null);
    setSelectedDok(null);
    setSelectedQuestionType(null);
    setDomains([]);
    setStandards([]);
    setIsPublished(undefined);
  };

  // Switching mode keeps both modes' values, so the user can flip back without
  // re-entering them. Hidden values never reach the request: the builder sends
  // only the active mode's fields. It sends no request by itself.
  const handleSearchModeChange = (mode: QuestionSearchMode) => {
    setSearchMode(mode);
  };

  const handleQuestionIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuestionIdText(sanitizeQuestionIdInput(e.target.value));
  };

  const handleReset = () => {
    clearCriteria();
    setQuestionIdText("");
    setSearchMode("criteria");
    paginationRef.current?.onReset();
    pageSizeRef.current?.onReset();
    setOrderByColumn(defaultOrderByColumn);
    setSortByDesc(defaultSortByDesc);
    search(buildDefaultRequest());
  };

  const handleSortBy = (column: string) => {
    const newSortByDesc = orderByColumn === column ? !sortByDesc : false;
    setOrderByColumn(column);
    setSortByDesc(newSortByDesc);
    paginationRef.current?.onReset();
    search(
      buildRequest({
        orderByColumn: column,
        sortByDesc: newSortByDesc,
        pageNumber: defaultPageNumber,
      })
    );
  };

  const handleCreateQuestion = () => {
    navigate(
      Constant.crmUserContentQuestionCreate.replace(
        ":userid",
        String(userId ?? "")
      )
    );
  };

  const renderSortableHeader = (
    label: string,
    column: string,
    className?: string
  ) => (
    <th
      scope="col"
      className={`px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider whitespace-nowrap${
        className ? ` ${className}` : ""
      }`}
    >
      <span className="flex items-center">
        {label}
        <span
          className="cursor-pointer ml-1 inline-flex"
          onClick={() => handleSortBy(column)}
        >
          <SortIcon
            direction={
              orderByColumn === column ? (sortByDesc ? "desc" : "asc") : "none"
            }
            className="w-2.5 h-4"
          />
        </span>
      </span>
    </th>
  );

  const questionsForPreview = teacherQuestions.map((q) => ({
    questionId: q.teacherQuestionId,
    englishQuestionReferenceId: `${q.teacherQuestionId}-teacher`,
  }));

  return (
    <>
      {loading && <Loader />}
      <div className="flex-1 overflow-auto">
        <div className="h-auto min-h-half w-full px-8 py-6">
          <div className="relative py-1">
            <form onSubmit={handleSearch}>
              <div className="pt-6 border-b pb-6">
                <fieldset className="mb-5">
                  <legend className="text-sm text-gray-700">Search by</legend>
                  <div className="flex items-center gap-6 mt-1">
                    <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="questionSearchMode"
                        value="criteria"
                        data-testid="search-mode-criteria"
                        checked={searchMode === "criteria"}
                        onChange={() => handleSearchModeChange("criteria")}
                        className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300"
                      />
                      Criteria
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="radio"
                        name="questionSearchMode"
                        value="questionId"
                        data-testid="search-mode-question-id"
                        checked={searchMode === "questionId"}
                        onChange={() => handleSearchModeChange("questionId")}
                        className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300"
                      />
                      Question ID
                    </label>
                  </div>
                </fieldset>

                {searchMode === "questionId" ? (
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-3 sm:col-span-3">
                      <label
                        htmlFor="questionId"
                        className="text-sm text-gray-700"
                      >
                        Question Id
                      </label>
                      <input
                        id="questionId"
                        name="questionId"
                        type="text"
                        inputMode="numeric"
                        maxLength={9}
                        data-testid="filter-question-id"
                        className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                        value={questionIdText}
                        onChange={handleQuestionIdChange}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-12 gap-6">
                      <div className="col-span-3 sm:col-span-3">
                        <label className="text-sm text-gray-700">Subject</label>
                        <Select
                          aria-label="Subject"
                          className="w-full mt-1 text-sm"
                          options={subjects}
                          onChange={(option) =>
                            handleSubjectChange(option as ISelectElement | null)
                          }
                          value={selectedSubject}
                          isClearable={true}
                          isSearchable={true}
                          theme={reactSelectTheme}
                        />
                      </div>

                      <div className="col-span-3 sm:col-span-3">
                        <label className="text-sm text-gray-700">Domain</label>
                        <Select
                          className="w-full mt-1 text-sm"
                          options={domains}
                          onChange={(option) =>
                            handleDomainChange(option as ISelectElement | null)
                          }
                          value={selectedDomain}
                          isClearable={true}
                          isSearchable={true}
                          isDisabled={!selectedSubject}
                          theme={reactSelectTheme}
                        />
                      </div>

                      <div className="col-span-3 sm:col-span-3">
                        <label className="text-sm text-gray-700">
                          Standard
                        </label>
                        <Select
                          className="w-full mt-1 text-sm"
                          options={standards}
                          onChange={(option) =>
                            setSelectedStandard(option as ISelectElement | null)
                          }
                          value={selectedStandard}
                          isClearable={true}
                          isSearchable={true}
                          isDisabled={!selectedDomain}
                          theme={reactSelectTheme}
                        />
                      </div>

                      <div className="col-span-3 sm:col-span-3">
                        <label className="text-sm text-gray-700">DOK</label>
                        <Select
                          className="w-full mt-1 text-sm"
                          options={dokOptions}
                          onChange={(option) =>
                            setSelectedDok(option as IDokOption | null)
                          }
                          value={selectedDok}
                          isClearable={true}
                          isSearchable={true}
                          theme={reactSelectTheme}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-12 gap-6 mt-5">
                      <div className="col-span-3 sm:col-span-3">
                        <label className="text-sm text-gray-700">
                          Question Type
                        </label>
                        <Select
                          className="w-full mt-1 text-sm"
                          options={questionTypes}
                          onChange={(option) =>
                            setSelectedQuestionType(
                              option as ISelectElement | null
                            )
                          }
                          value={selectedQuestionType}
                          isClearable={true}
                          isSearchable={true}
                          theme={reactSelectTheme}
                        />
                      </div>

                      <div className="col-span-3 sm:col-span-3 flex items-center">
                        <input
                          id="isPublished"
                          name="isPublished"
                          type="checkbox"
                          ref={(el) => {
                            if (el)
                              el.indeterminate = isPublished === undefined;
                          }}
                          checked={isPublished === true}
                          onChange={() =>
                            setIsPublished((prev) => publishedNextState(prev))
                          }
                          className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                        />
                        <label
                          htmlFor="isPublished"
                          className="ml-1.5 text-sm text-gray-700"
                        >
                          Published ({publishedLabel(isPublished)})
                        </label>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center">
                  <div className="flex">
                    <button
                      type="submit"
                      className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                    >
                      <FilterIcon className="w-3 h-3" />
                      <span>Search</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                    >
                      <ResetIcon className="w-3 h-3" />
                      <span>Reset</span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateQuestion}
                    className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  >
                    <span>Create Question</span>
                  </button>
                </div>
              </div>
            </form>

            <div id="dataContainer" className="mt-4">
              {teacherQuestions.length === 0 ? (
                <EmptyResults
                  title="No matching questions"
                  subtitle="Adjust your filters and try again."
                />
              ) : (
                <div className="flex flex-col mt-1">
                  <div className="-my-2 relative sm:-mx-6 lg:-mx-8">
                    <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                      <div className="shadow border-b border-gray-200 sm:rounded-lg">
                        <table className="min-w-full table-fixed divide-y divide-gray-200">
                          <thead className="bg-primary-color">
                            <tr>
                              {renderSortableHeader("Id", "QuestionId", "w-20")}
                              {renderSortableHeader(
                                "Created Date",
                                "CreatedDate",
                                "w-32"
                              )}
                              {renderSortableHeader(
                                "Question Type",
                                "QuestionType"
                              )}
                              {renderSortableHeader("Subject", "Subject")}
                              <th
                                scope="col"
                                className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider whitespace-nowrap"
                              >
                                Domain
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 font-light text-sm text-left text-white dark:text-white tracking-wider whitespace-nowrap"
                              >
                                Standard
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-24 font-light text-sm text-center text-white dark:text-white tracking-wider whitespace-nowrap"
                              >
                                <span className="flex items-center justify-center">
                                  Owner
                                  <span
                                    className="cursor-pointer ml-1 inline-flex"
                                    onClick={() => handleSortBy("Owner")}
                                  >
                                    <SortIcon
                                      direction={
                                        orderByColumn === "Owner"
                                          ? sortByDesc
                                            ? "desc"
                                            : "asc"
                                          : "none"
                                      }
                                      className="w-2.5 h-4"
                                    />
                                  </span>
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-24 font-light text-sm text-center text-white dark:text-white tracking-wider whitespace-nowrap"
                              >
                                <span className="flex items-center justify-center">
                                  Author
                                  <span
                                    className="cursor-pointer ml-1 inline-flex"
                                    onClick={() => handleSortBy("Author")}
                                  >
                                    <SortIcon
                                      direction={
                                        orderByColumn === "Author"
                                          ? sortByDesc
                                            ? "desc"
                                            : "asc"
                                          : "none"
                                      }
                                      className="w-2.5 h-4"
                                    />
                                  </span>
                                </span>
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 w-20 font-light text-sm text-center text-white dark:text-white tracking-wider rounded-tr-lg"
                              >
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {teacherQuestions.map((question, index) => (
                              <tr
                                key={question.teacherQuestionId}
                                className={
                                  index % 2 === 0 ? "bg-white" : "bg-gray-100"
                                }
                              >
                                <td className="px-3 py-2 text-sm font-normal text-gray-700">
                                  {question.teacherQuestionId}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {question.createdDate
                                    ? new Date(
                                        question.createdDate
                                      ).toLocaleDateString()
                                    : ""}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {question.questionTypeName}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {question.subjectName}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {question.domainName}
                                </td>
                                <td className="px-3 py-2 text-sm font-normal text-gray-500">
                                  {question.standardCode
                                    ? `[${question.standardCode}] `
                                    : ""}
                                  {question.standardName}
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <InitialsBadge
                                    firstName={question.ownerFirstName}
                                    lastName={question.ownerLastName}
                                    userId={question.ownerUserId}
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <InitialsBadge
                                    firstName={question.authorFirstName}
                                    lastName={question.authorLastName}
                                    userId={question.authorUserId ?? null}
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm text-center font-normal text-gray-500">
                                  <div className="flex justify-center gap-2">
                                    <EditIcon
                                      title="Edit question"
                                      onClick={() =>
                                        navigate(
                                          `/crm/users/${userId}/content/questions/${question.teacherQuestionId}`
                                        )
                                      }
                                      className="h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                    <PreviewIcon
                                      title="Preview question"
                                      onClick={() => {
                                        setPreviewItemRef(
                                          `${question.teacherQuestionId}-teacher`
                                        );
                                        setQuestionIdPreview(
                                          question.teacherQuestionId
                                        );
                                        setQuestionIndexPreview(index);
                                        setPreviewOpen(true);
                                      }}
                                      className="h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center mt-2">
                    {totalPages > 1 && (
                      <Pagination
                        pages={totalPages}
                        setPageNumber={setPageNumber}
                        ref={paginationRef}
                      />
                    )}
                    <span className="ml-auto">
                      <span className="text-sm pr-4 ml-auto text-left-700">
                        Showing {pageNumber} to{" "}
                        {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                        {totalRecords} results
                      </span>
                      <SelectPageSize
                        setPageSize={setPageSize}
                        type={"second"}
                        ref={pageSizeRef}
                        defaultPage={defaultPageSize}
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {previewOpen && (
        <PreviewModal
          show={previewOpen}
          changeValue={(val: boolean) => setPreviewOpen(val)}
          itemRef={previewItemRef}
          isTeacherQuestion={true}
          questionIdPreview={questionIdPreview}
          questionIndexPreview={questionIndexPreview}
          setQuestionIndexPreview={setQuestionIndexPreview}
          setQuestionIdPreview={setQuestionIdPreview}
          setPreviewId={setPreviewItemRef}
          setQuestionToPreview={() => undefined}
          teacherQuestionOwnerUserId={userId}
          questions={questionsForPreview}
        />
      )}
    </>
  );
};
